import React, { useEffect, useRef, useState } from "react";
import "./EditableNextGlobalImprovementsSurvey.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  updateSurveyData,
  updateSurveyPage,
} from "../../../../../redux/actions/survey";
import SurveyHero from "../../../../components/Survey/SurveyHero/SurveyHero";
import HealthConcernText from "../../../../components/Survey/HealthConcernText/HealthConcernText";
import getThemes from "../../../../../constants/colors/getTheme";
import Button from "../../../../components/Comon/Button/Button";
import LinkText from "../../../../components/Comon/LinkText/LinkText";
import { resetPopUp, showPopup } from "../../../../../redux/actions/app";
import { updateSurvey } from "../../../../../core/axios/services";
import Loader from "../../../../components/Loader/Loader";
import ScoreChangePromptModal from "../../../../components/Survey/ScoreChangePromptModal";
import {
  getConcernFieldByNameOrIndex,
  patchSurveyDataArrayItem,
} from "../../utils/editPayload";

const PREVIOUS_RATE_FIELD_BY_NAME = {
  NECK: "globalHealthNeck",
  "MID BACK": "globalHealthMidBack",
  "LOWER BACK": "globalHealthLowerBack",
  POSTURE: "globalHealthPosture",
  "ENERGY LEVELS": "globalHealthEnergyLevels",
  "ABILITY TO HANDLE STRESS": "globalHealthAbilityStress",
  "QUALITY OF SLEEP": "globalHealthSleepQuality",
  "IMMUNE SYSTEM": "globalHealthImmuneSystem",
  "MENTAL STATE": "globalHealthMentalState",
  "EMOTIONAL STATE": "globalHealthEmotionalState",
};

const EditableNextGlobalImprovementsSurvey = ({
  showGlobalSnackbar,
  survey,
  i18n,
  app,
}) => {
  const [newScale, setNewScale] = useState(undefined);
  const [concerns, setConcerns] = useState([]);
  const [themeColor, setThemeColor] = useState([]);
  const [toSave, setToSave] = useState(false);
  const [isSendingSurveyToApi, setIsSendingSurveyToApi] = useState(false);
  const [scorePromptShown, setScorePromptShown] = useState(false);
  const scorePromptShownRef = useRef(false);
  const [scorePrompt, setScorePrompt] = useState({
    open: false,
    index: null,
    previousRate: null,
    currentRate: null,
  });
  const initializedRef = useRef(false);
  const navigate = useNavigate();
  const { code } = useParams();
  const { previousId } = useParams();
  const { surveyId } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (survey?.previousSurveyData?.scaleType || survey?.data?.scaleType) {
      setNewScale(
        (survey?.previousSurveyData?.scaleType ?? survey?.data?.scaleType) ===
          "zero_scale"
      );
    }
  }, [survey]);

  const getOveralHealthForCurrentLanguage = () => {
    return survey?.settings?.clinic?.overallHealths.find(
      (item) => item.language.id === survey?.settings?.language?.id
    );
  };

  const getGlobalHealthField = (name, fallbackIndex, field) =>
    getConcernFieldByNameOrIndex(
      survey?.data?.globalHealth,
      name,
      fallbackIndex,
      field
    );

  const getPreviousRateForMetric = (metricName, fallbackIndex) => {
    const namedRate = getConcernFieldByNameOrIndex(
      survey?.previousSurveyData?.globalHealth,
      metricName,
      fallbackIndex,
      "concernRate"
    );
    if (namedRate !== undefined && namedRate !== null) {
      return namedRate;
    }

    const fieldName = PREVIOUS_RATE_FIELD_BY_NAME[(metricName || "").toUpperCase()];
    if (!fieldName) return undefined;
    return survey?.previousSurveyData?.[fieldName];
  };

  const buildGlobalHealthConcerns = () => {
    const overallHealth = getOveralHealthForCurrentLanguage();
    const bodyMetrics = Array.isArray(survey?.settings?.bodyMetrics)
      ? survey.settings.bodyMetrics
      : [];
    const bodyMetricCount = bodyMetrics.length;

    const bodyMetricConcerns = bodyMetrics.map((metric, index) => {
      const previousRate = getPreviousRateForMetric(metric.title, index);
      return {
        name: metric.title,
        topText: metric.title,
        headline: metric.question,
        text:
          previousRate !== undefined && previousRate !== null
            ? i18n.t("youRatedItLastTime", { rate: previousRate })
            : undefined,
        lastTimeRate: previousRate,
        concernRate: getGlobalHealthField(metric.title, index, "concernRate"),
        leftOption: metric.lowScaleName,
        rightOption: metric.highScaleName,
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      };
    });

    const endConcerns = [
      {
        name: "overallCurrent",
        topText: overallHealth?.question1Subtitle,
        headline: overallHealth?.question1Title,
        text: "",
        lastTimeRate: survey?.previousSurveyData?.globalHealthOverallCurrent,
        leftOption: overallHealth?.question1LowScale,
        rightOption: overallHealth?.question1HighScale,
        concernRate: getGlobalHealthField(
          "overallCurrent",
          bodyMetricCount,
          "concernRate"
        ),
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      },
      {
        name: "overallDesired",
        topText: overallHealth?.question2Subtitle,
        headline: overallHealth?.question2Title,
        text: "",
        lastTimeRate: survey?.previousSurveyData?.globalHealthOverallDesired,
        leftOption: overallHealth?.question2LowScale,
        rightOption: overallHealth?.question2HighScale,
        concernRate: getGlobalHealthField(
          "overallDesired",
          bodyMetricCount + 1,
          "concernRate"
        ),
        mandatory: false,
        hasTextInput: false,
        hasRate: true,
      },
    ];

    return [...bodyMetricConcerns, ...endConcerns];
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(updateSurveyPage(2));
  }, [dispatch]);

  useEffect(() => {
    initializedRef.current = false;
  }, [surveyId]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!survey?.settings?.bodyMetrics || !survey?.data || !survey?.previousSurveyData)
      return;

    const builtConcerns = buildGlobalHealthConcerns();
    if (!builtConcerns.length) return;

    setConcerns(builtConcerns);
    setScorePromptShown(false);
    scorePromptShownRef.current = false;
    setScorePrompt({
      open: false,
      index: null,
      previousRate: null,
      currentRate: null,
    });
    dispatch(
      updateSurveyData({
        ...survey.data,
        globalHealth: builtConcerns,
      })
    );

    const activeQuestions =
      survey?.settings?.openEndedQuestions?.filter((o) => o.isActive === true)
        .length || 0;
    setToSave(activeQuestions === 0);
    initializedRef.current = true;
  }, [survey, i18n, dispatch]);

  useEffect(() => {
    if (app.popupStatus === true) {
      switch (app.popUpData?.type) {
        case "editSurvey":
          sendSurveyToApi();
          dispatch(resetPopUp());
          break;
        default:
          break;
      }
    } else if (app.popupStatus === false) {
      dispatch(resetPopUp());
    }

    setThemeColor(getThemes("mainColor", app.appTheme));
  }, [app, dispatch]);

  const sendSurveyToApi = async () => {
    setIsSendingSurveyToApi(true);
    try {
      const surveyPayload = {
        ...survey,
        data: {
          ...(survey?.data || {}),
          globalHealth: concerns,
        },
      };
      return await updateSurvey(surveyPayload).then(() => {
        setIsSendingSurveyToApi(false);
        dispatch(updateSurveyPage(5));
        navigate(
          "/survey-next-edit/" +
            code +
            "/" +
            surveyId +
            "/" +
            previousId +
            "/thank-you"
        );
      });
    } catch (error) {
      setIsSendingSurveyToApi(false);
      if (error.response?.data?.errorMessage) {
        showGlobalSnackbar(error.response.data.errorMessage);
      } else {
        showGlobalSnackbar(i18n.t("thereWasAnError"));
      }
    }
  };

  const checkIfBodyMetricsSelected = () => {
    return concerns.every(
      (concern) =>
        concern?.hasRate !== true ||
        (concern?.concernRate !== undefined && concern?.concernRate !== null)
    );
  };

  const save = () => {
    if (!checkIfBodyMetricsSelected()) {
      scrollTo();
      showGlobalSnackbar(i18n.t("pleaseCompleteBodyMetrics"), 2000);
      return;
    }
    const data = {
      showPopup: true,
      data: {
        title: i18n.t("confirmEditSurvey"),
        confirmButton: i18n.t("confirmEditSurveyButton"),
        denyButton: i18n.t("denyEditSurveyButton"),
        type: "editSurvey",
      },
    };
    dispatch(showPopup(data));
  };

  const nextPage = () => {
    if (!checkIfBodyMetricsSelected()) {
      scrollTo();
      showGlobalSnackbar(i18n.t("pleaseCompleteBodyMetrics"), 2000);
      return;
    }
    dispatch(
      updateSurveyData({
        ...(survey?.data || {}),
        globalHealth: concerns,
      })
    );
    dispatch(updateSurveyPage(3));
    navigate(
      "/survey-next-edit/" +
        code +
        "/" +
        surveyId +
        "/" +
        previousId +
        "/health-improvements"
    );
  };

  const previousPage = () => {
    dispatch(updateSurveyPage(1));
    navigate(
      "/survey-next-edit/" +
        code +
        "/" +
        surveyId +
        "/" +
        previousId +
        "/health-concerns"
    );
  };

  const updateConcern = (index, value, type) => {
    const fieldName = type === "text" ? "concern" : "concernRate";
    const nextConcerns = concerns.map((concern, concernIndex) =>
      concernIndex === index ? { ...concern, [fieldName]: value } : concern
    );

    if (type === "rate" && !scorePromptShownRef.current) {
      const previousRate = Number(nextConcerns[index]?.lastTimeRate);
      const currentRate = Number(value);
      const hasValidRates =
        Number.isFinite(previousRate) && Number.isFinite(currentRate);
      const concernScaleVariant = isBodyMetricConcern(nextConcerns[index]?.name)
        ? globalImprovementsScaleVariant
        : "regular";
      const hasNegativeSwing =
        hasValidRates &&
        concernScaleVariant === "red_to_green" &&
        previousRate - currentRate >= 2;

      if (hasNegativeSwing) {
        scorePromptShownRef.current = true;
        setScorePromptShown(true);
        setConcerns(nextConcerns);
        setScorePrompt({
          open: true,
          index,
          previousRate,
          currentRate,
        });
        return;
      }
    }

    setConcerns(nextConcerns);

    const nextData = patchSurveyDataArrayItem({
      data: survey.data,
      arrayKey: "globalHealth",
      name: nextConcerns[index]?.name,
      fallbackIndex: index,
      patch: { [fieldName]: value },
    });

    dispatch(
      updateSurveyData({
        ...(nextData || survey?.data || {}),
        globalHealth: nextConcerns,
      })
    );
  };

  const closeScorePrompt = () => {
    dispatch(
      updateSurveyData({
        ...(survey?.data || {}),
        globalHealth: concerns,
      })
    );
    setScorePrompt((prev) => ({ ...prev, open: false }));
  };

  const changeScoreAnswer = () => {
    if (scorePrompt.index === null || scorePrompt.index === undefined) {
      closeScorePrompt();
      return;
    }

    const nextConcerns = concerns.map((concern, concernIndex) =>
      concernIndex === scorePrompt.index
        ? { ...concern, concernRate: undefined }
        : concern
    );
    setConcerns(nextConcerns);

    const nextData = patchSurveyDataArrayItem({
      data: survey.data,
      arrayKey: "globalHealth",
      name: nextConcerns[scorePrompt.index]?.name,
      fallbackIndex: scorePrompt.index,
      patch: { concernRate: undefined },
    });

    dispatch(
      updateSurveyData({
        ...(nextData || survey?.data || {}),
        globalHealth: nextConcerns,
      })
    );
    closeScorePrompt();
  };

  const scrollTo = () => {
    if (!concerns.length) return;
    const element = document.getElementById(concerns[0].name);
    element?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  const isBodyMetricConcern = (concernName) =>
    !["howLongToAchieve", "markThings"].includes(concernName);
  const globalImprovementsScaleVariant = "red_to_green";

  return (
    <div className="ThirdScreenSurvey">
      <ScoreChangePromptModal
        open={scorePrompt.open}
        previousRate={scorePrompt.previousRate}
        currentRate={scorePrompt.currentRate}
        onConfirm={closeScorePrompt}
        onChangeAnswer={changeScoreAnswer}
      />
      <SurveyHero
        headline={getOveralHealthForCurrentLanguage()?.nextTitle}
        text={getOveralHealthForCurrentLanguage()?.nextDesc}
        videoLink={getOveralHealthForCurrentLanguage()?.nextVideoUrl}
        buttonText={i18n.t("next")}
        clickedButton={() => scrollTo()}
        app={app}
        surveyType={survey?.settings?.type}
      />

      <div className={"concernsContainer"}>
        {concerns.map((concern, index) => {
          return (
            <div className={"concernContainer"} key={index}>
              <HealthConcernText
                newScale={newScale}
                scaleVariant={
                  isBodyMetricConcern(concern.name)
                    ? globalImprovementsScaleVariant
                    : "regular"
                }
                setConcernText={(value) => updateConcern(index, value, "text")}
                setConcernRate={(value) => updateConcern(index, value, "rate")}
                mandatory={concern.mandatory}
                text={concern.text}
                concernRate={concern.concernRate}
                topText={concern.topText}
                headline={concern.headline}
                themeColor={themeColor}
                hasTextInput={false}
                leftOption={concern.leftOption}
                rightOption={concern.rightOption}
                id={concern.name}
                i18n={i18n}
                lastTimeRate={concern.lastTimeRate}
              />
            </div>
          );
        })}
      </div>
      {isSendingSurveyToApi && <Loader />}
      <div className={"buttonsContainer"}>
        {toSave ? (
          <div className={"nextContainer"} onClick={() => save()}>
            <Button
              disabled={isSendingSurveyToApi}
              color={themeColor}
              mode={"small"}
              text={i18n.t("completeSurvey")}
            />
          </div>
        ) : (
          <div className={"nextContainer"} onClick={() => nextPage()}>
            <Button
              disabled={isSendingSurveyToApi}
              color={themeColor}
              mode={"small"}
              text={i18n.t("next")}
            />
          </div>
        )}
        <div className={"previousContainer"} onClick={() => previousPage()}>
          <LinkText text={i18n.t("previous")} />
        </div>
      </div>
    </div>
  );
};

export default EditableNextGlobalImprovementsSurvey;
